import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  ButtonBase,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Link,
  Typography,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

const PKG_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Parses Firestore Timestamps, {seconds}, or various string date formats.
// Mirrors the parsing approach already used in Payment.js so date handling stays consistent.
const parseAnyDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  if (raw.seconds) return new Date(raw.seconds * 1000);
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/ at /i, ' ').replace(/ utc-4/i, ' GMT-0400').replace(/ utc/i, ' GMT');
    let d = new Date(cleaned);
    if (isNaN(d.getTime())) d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// A driver's job is done once the package is dropped off, whether or not money
// changed hands at the door. recordPayment() writes status 'delivered', but
// 'paid' is separately selectable from the status editor, so counting only
// 'delivered' silently drops every manually-paid package out of the totals.
const COMPLETED_STATUSES = new Set(['delivered', 'paid']);
const isCompleted = (pkg) => COMPLETED_STATUSES.has((pkg.status || '').toLowerCase());

// Same status set/colors as PackageDashboard, UserPackageDetail, etc. so badges stay consistent app-wide
const STATUS_CHIP_STYLES = {
  delivered: { bgcolor: '#dcfce7', color: '#166534' },
  'in-transit': { bgcolor: '#dbeafe', color: '#1e40af' },
  pending: { bgcolor: '#f3f4f6', color: '#1f2937' },
  returned: { bgcolor: '#fef9c3', color: '#9a3412' },
  'in-store': { bgcolor: '#f3f4f6', color: '#1f2937' },
  canceled: { bgcolor: '#fee2e2', color: '#991b1b' },
  'left-store': { bgcolor: '#dbeafe', color: '#1e40af' },
  paid: { bgcolor: '#f3e8ff', color: '#6b21a8' },
  'returning-to-store': { bgcolor: '#fef9c3', color: '#854d0e' },
};

const STATUS_LIST = [
  { id: 'delivered', label: 'Delivered' },
  { id: 'in-transit', label: 'In Transit' },
  { id: 'pending', label: 'Pending' },
  { id: 'returned', label: 'Returned' },
  { id: 'in-store', label: 'In Store' },
  { id: 'canceled', label: 'Canceled' },
  { id: 'left-store', label: 'Left Store' },
  { id: 'paid', label: 'Paid' },
  { id: 'returning-to-store', label: 'Returning to Store' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

// Small KPI tile, matches the stat-card language used elsewhere (Paper outlined + label/value)
const StatCard = ({ label, value, color = 'text.primary', caption }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} sx={{ color, mt: 0.5 }}>{value}</Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

const DriverDeliveryDashboard = () => {
  const [authUser] = useAuthState(auth);

  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // The tab, the selected driver and the status filter live in the URL so that
  // coming back from a package detail page lands on the same view you left,
  // rather than resetting to the Overview tab.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(
    () => (searchParams.get('tab') === 'analytics' ? 'analytics' : 'overview')
  ); // 'overview' | 'analytics'

  // Overview tab filters (independent from Analytics tab filters)
  const [overviewDriver, setOverviewDriver] = useState('all');
  const [overviewStart, setOverviewStart] = useState('');
  const [overviewEnd, setOverviewEnd] = useState('');

  // Analytics tab filters (its own separate date range picker instance)
  const [analyticsDriver, setAnalyticsDriver] = useState(() => searchParams.get('driver') || 'all');
  const [analyticsStart, setAnalyticsStart] = useState('');
  const [analyticsEnd, setAnalyticsEnd] = useState('');

  // Assigned-packages table. The status breakdown doubles as this table's
  // filter, so clicking a status tile narrows the list beneath it.
  const [packageStatusFilter, setPackageStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [pkgPage, setPkgPage] = useState(0);
  const [pkgRowsPerPage, setPkgRowsPerPage] = useState(PKG_ROWS_PER_PAGE_OPTIONS[0]);

  // Cursor pagination for the assigned-packages table. A page is fetched the
  // first time it is asked for and then cached, so stepping back costs nothing.
  //
  // Only used when a single driver is selected: "assigned to anyone" has no
  // clean equality filter, so All Drivers still slices the in-memory list.
  const [pkgPages, setPkgPages] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgError, setPkgError] = useState(null);
  const pkgCursorsRef = useRef([]);

  const serverPaged = analyticsDriver !== 'all';

  // Mirror the view into the URL. replace rather than push: paging and filtering
  // should not each cost a press of the browser's back button — one press should
  // leave the dashboard entirely.
  //
  // The page number is deliberately not stored. Cursor pagination has no cursor
  // for an arbitrary page, so a restored "page 3" could not be honoured without
  // re-fetching every page before it.
  useEffect(() => {
    const next = {};
    if (activeTab !== 'overview') next.tab = activeTab;
    if (analyticsDriver !== 'all') next.driver = analyticsDriver;
    if (packageStatusFilter !== 'all') next.status = packageStatusFilter;
    setSearchParams(next, { replace: true });
  }, [activeTab, analyticsDriver, packageStatusFilter, setSearchParams]);

  // Where a package link should return to, captured at render time.
  const backToHere = `${location.pathname}${location.search}`;

  // Clicking a driver on Overview carries them into Analytics, which is where
  // their package list lives.
  const showDriverAnalytics = (driverId) => {
    setAnalyticsDriver(driverId);
    setPackageStatusFilter('all');
    setPkgPage(0);
    setActiveTab('analytics');
  };

  useEffect(() => {
    const fetchAll = async () => {
      if (!authUser) return;
      setLoading(true);
      try {
        const [usersSnap, packagesSnap, paymentsSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'packages')),
          getDocs(collection(db, 'payments')),
        ]);

        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPackages(packagesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPayments(paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching driver delivery data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [authUser]);

  // Only users with the 'driver' role show up in the dropdowns and tables
  const drivers = useMemo(
    () => users.filter(u => Array.isArray(u.roles) && u.roles.includes('driver')),
    [users]
  );

  const driverName = (driverId) => {
    const d = drivers.find(u => u.id === driverId);
    return d?.name || d?.displayName || d?.email || 'Unknown Driver';
  };

  const withinRange = (date, start, end) => {
    if (!date) return !start && !end;
    if (start) {
      const s = new Date(start);
      s.setHours(0, 0, 0, 0);
      if (date < s) return false;
    }
    if (end) {
      const e = new Date(end);
      e.setHours(23, 59, 59, 999);
      if (date > e) return false;
    }
    return true;
  };

  // ---------- Overview tab data ----------
  const overviewPackages = useMemo(() => {
    return packages.filter(pkg => {
      const created = parseAnyDate(pkg.createdAt);
      return withinRange(created, overviewStart, overviewEnd);
    });
  }, [packages, overviewStart, overviewEnd]);

  const overviewRows = useMemo(() => {
    return drivers.map(driver => {
      const assigned = overviewPackages.filter(p => p.assignedTo === driver.id);
      const delivered = assigned.filter(isCompleted);
      return {
        driverId: driver.id,
        name: driver.name || driver.displayName || driver.email || 'Unknown Driver',
        email: driver.email || 'N/A',
        assignedCount: assigned.length,
        deliveredCount: delivered.length,
        deliveryRate: assigned.length > 0 ? (delivered.length / assigned.length) * 100 : 0,
      };
    });
  }, [drivers, overviewPackages]);

  const visibleOverviewRows = overviewDriver === 'all'
    ? overviewRows
    : overviewRows.filter(r => r.driverId === overviewDriver);

  const systemAssigned = overviewPackages.filter(p => p.assignedTo).length;
  const systemDelivered = overviewPackages.filter(isCompleted).length;
  const systemDeliveryRate = systemAssigned > 0 ? (systemDelivered / systemAssigned) * 100 : 0;

  // ---------- Analytics tab data ----------
  const analyticsPackages = useMemo(() => {
    return packages.filter(pkg => {
      const created = parseAnyDate(pkg.createdAt);
      return withinRange(created, analyticsStart, analyticsEnd);
    });
  }, [packages, analyticsStart, analyticsEnd]);

  // Memoised because three downstream memos take it as a dependency — as a bare
  // const its identity changed every render and they all recomputed with it.
  const analyticsScopedPackages = useMemo(
    () => (analyticsDriver === 'all'
      ? analyticsPackages.filter(p => p.assignedTo)
      : analyticsPackages.filter(p => p.assignedTo === analyticsDriver)),
    [analyticsPackages, analyticsDriver]
  );

  const statusCounts = useMemo(() => {
    const counts = {};
    STATUS_LIST.forEach(s => { counts[s.id] = 0; });
    analyticsScopedPackages.forEach(pkg => {
      const key = (pkg.status || '').toLowerCase();
      if (counts[key] !== undefined) counts[key] += 1;
    });
    return counts;
  }, [analyticsScopedPackages]);

  // Dollar value of deliveries made: payments tied to this scope's completed
  // packages, filtered independently by the Analytics tab's own date range (on
  // paymentDate). Packages sitting at status 'paid' are exactly the ones most
  // likely to carry a payment, so they have to be in this set.
  const deliveredPackageIds = useMemo(
    () => new Set(analyticsScopedPackages.filter(isCompleted).map(p => p.id)),
    [analyticsScopedPackages]
  );

  const scopedPayments = useMemo(() => {
    return payments.filter(pmt => {
      if (!deliveredPackageIds.has(pmt.packageId)) return false;
      const paidOn = parseAnyDate(pmt.paymentDate);
      return withinRange(paidOn, analyticsStart, analyticsEnd);
    });
  }, [payments, deliveredPackageIds, analyticsStart, analyticsEnd]);

  const totalDeliveryValue = scopedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Straight from the package documents themselves (total/cost field), rather than the payments join above.
  // Checks both field names since it varies by how the package was created.
  const deliveredPackagesInScope = analyticsScopedPackages.filter(isCompleted);
  const totalPackageValue = deliveredPackagesInScope.reduce((sum, p) => sum + (Number(p.total) || Number(p.cost) || 0), 0);

  // The packages themselves, newest first, optionally narrowed to one status by
  // the breakdown tiles above the table.
  const assignedPackages = useMemo(() => {
    const list = packageStatusFilter === 'all'
      ? analyticsScopedPackages
      : analyticsScopedPackages.filter(p => (p.status || '').toLowerCase() === packageStatusFilter);
    return [...list].sort(
      (a, b) => (parseAnyDate(b.createdAt)?.getTime() || 0) - (parseAnyDate(a.createdAt)?.getTime() || 0)
    );
  }, [analyticsScopedPackages, packageStatusFilter]);

  const pagedPackages = useMemo(
    () => assignedPackages.slice(pkgPage * pkgRowsPerPage, pkgPage * pkgRowsPerPage + pkgRowsPerPage),
    [assignedPackages, pkgPage, pkgRowsPerPage]
  );

  // Keep the page in range when a filter shrinks the list under it.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(assignedPackages.length / pkgRowsPerPage) - 1);
    if (pkgPage > lastPage) setPkgPage(lastPage);
  }, [assignedPackages.length, pkgRowsPerPage, pkgPage]);

  // Fetch one page of a single driver's packages. One extra document tells us
  // whether a further page exists without paying for a second query.
  const loadPackagePage = useCallback(async (index) => {
    if (!authUser || analyticsDriver === 'all') return;

    setPkgLoading(true);
    setPkgError(null);
    try {
      const constraints = [where('assignedTo', '==', analyticsDriver)];
      if (packageStatusFilter !== 'all') constraints.push(where('status', '==', packageStatusFilter));
      if (analyticsStart) {
        const s = new Date(analyticsStart);
        s.setHours(0, 0, 0, 0);
        constraints.push(where('createdAt', '>=', s));
      }
      if (analyticsEnd) {
        const e = new Date(analyticsEnd);
        e.setHours(23, 59, 59, 999);
        constraints.push(where('createdAt', '<=', e));
      }
      constraints.push(orderBy('createdAt', 'desc'));

      const cursor = index > 0 ? pkgCursorsRef.current[index - 1] : null;
      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(limit(pkgRowsPerPage + 1));

      const snapshot = await getDocs(query(collection(db, 'packages'), ...constraints));
      const docs = snapshot.docs.slice(0, pkgRowsPerPage);
      pkgCursorsRef.current[index] = docs[docs.length - 1] || null;

      setPkgPages(prev => {
        const next = [...prev];
        next[index] = {
          items: docs.map(d => ({ id: d.id, ...d.data() })),
          hasMore: snapshot.docs.length > pkgRowsPerPage,
        };
        return next;
      });
      setPkgPage(index);
    } catch (err) {
      console.error('Error loading driver packages:', err);
      setPkgError('Could not load this driver\'s packages. The index may still be building.');
    } finally {
      setPkgLoading(false);
    }
  }, [authUser, analyticsDriver, packageStatusFilter, analyticsStart, analyticsEnd, pkgRowsPerPage]);

  // Every one of these invalidates the cursors already collected, so the cache
  // is dropped and page 0 re-fetched.
  useEffect(() => {
    if (activeTab !== 'analytics' || analyticsDriver === 'all') return;
    pkgCursorsRef.current = [];
    setPkgPages([]);
    setPkgPage(0);
    loadPackagePage(0);
  }, [activeTab, analyticsDriver, loadPackagePage]);

  // Rows come from the server for a single driver, from memory for All Drivers.
  // The count is known either way, because the in-memory set is already loaded
  // for the breakdown above — so the pager shows a real total, not "more than".
  const tableRows = serverPaged ? (pkgPages[pkgPage]?.items || []) : pagedPackages;

  const handlePkgPageChange = (event, nextIndex) => {
    if (nextIndex < 0) return;
    if (!serverPaged || pkgPages[nextIndex]) {
      setPkgPage(nextIndex);
      return;
    }
    loadPackagePage(nextIndex);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1280, xl: 1600 }, p: { xs: 2, md: 3 }, px: { lg: 4 } }}>
        <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: { xs: 2, md: 3 } }}>
          Driver Delivery Performance
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="analytics" label="Driver Analytics" />
        </Tabs>

        {loading ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={22} sx={{ mr: 1.5, verticalAlign: 'middle' }} />
            <Typography component="span" color="text.secondary">Loading delivery data...</Typography>
          </Paper>
        ) : activeTab === 'overview' ? (
          <>
            {/* System-wide totals, unaffected by the driver dropdown below */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              <StatCard label="Total Packages Assigned (System)" value={systemAssigned} color="#2563eb" />
              <StatCard
                label="Total Delivered (System)"
                value={systemDelivered}
                color="#16a34a"
                caption="Delivered or paid"
              />
              <StatCard label="System Delivery Rate" value={`${systemDeliveryRate.toFixed(1)}%`} color="#7c3aed" />
            </Box>

            {/* Filters */}
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <TextField
                  select
                  label="Driver"
                  value={overviewDriver}
                  onChange={(e) => setOverviewDriver(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="all">All Drivers</MenuItem>
                  {drivers.map(d => (
                    <MenuItem key={d.id} value={d.id}>{d.name || d.displayName || d.email}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="From date"
                  type="date"
                  value={overviewStart}
                  onChange={(e) => setOverviewStart(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="To date"
                  type="date"
                  value={overviewEnd}
                  onChange={(e) => setOverviewEnd(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ClearIcon />}
                  onClick={() => { setOverviewDriver('all'); setOverviewStart(''); setOverviewEnd(''); }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
              </Stack>
            </Paper>

            {/* Per-driver table */}
            {visibleOverviewRows.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">No drivers match your filters.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                      <TableCell>Driver</TableCell>
                      <TableCell>Packages Assigned</TableCell>
                      <TableCell>Delivered or Paid</TableCell>
                      <TableCell>Delivery Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleOverviewRows.map(row => (
                      // The row is the way into that driver's package list.
                      <TableRow
                        key={row.driverId}
                        hover
                        onClick={() => showDriverAnalytics(row.driverId)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={(e) => {
                              e.stopPropagation();
                              showDriverAnalytics(row.driverId);
                            }}
                            sx={{ display: 'block', textAlign: 'left', p: 0 }}
                          >
                            <Typography variant="body2" fontWeight={500} color="text.primary">
                              {row.name}
                            </Typography>
                          </Link>
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.assignedCount}</TableCell>
                        <TableCell>{row.deliveredCount}</TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(row.deliveryRate, 100)}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 42 }}>
                              {row.deliveryRate.toFixed(1)}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <>
            {/* Analytics tab filters */}
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <TextField
                  select
                  label="Driver"
                  value={analyticsDriver}
                  onChange={(e) => setAnalyticsDriver(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="all">All Drivers</MenuItem>
                  {drivers.map(d => (
                    <MenuItem key={d.id} value={d.id}>{d.name || d.displayName || d.email}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="From date"
                  type="date"
                  value={analyticsStart}
                  onChange={(e) => setAnalyticsStart(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="To date"
                  type="date"
                  value={analyticsEnd}
                  onChange={(e) => setAnalyticsEnd(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ClearIcon />}
                  onClick={() => { setAnalyticsDriver('all'); setAnalyticsStart(''); setAnalyticsEnd(''); }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
              </Stack>
            </Paper>

            {/* Dollar value of deliveries */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
              <StatCard
                label={`Total Payments Received ${analyticsDriver === 'all' ? '(All Drivers)' : `(${driverName(analyticsDriver)})`}`}
                value={formatCurrency(totalDeliveryValue)}
                color="#16a34a"
                caption="From the payments collection, linked to packages marked delivered or paid"
              />
              <StatCard
                label={`Total Package Value ${analyticsDriver === 'all' ? '(All Drivers)' : `(${driverName(analyticsDriver)})`}`}
                value={formatCurrency(totalPackageValue)}
                color="#2563eb"
                caption="Sum of the total/cost field on packages marked delivered or paid"
              />
            </Box>

            {/* Status breakdown */}
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                Package Status Breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
                {STATUS_LIST.map(s => {
                  const active = packageStatusFilter === s.id;
                  return (
                    // Each tile toggles the table below between that status and
                    // everything, so the breakdown is also the way to answer
                    // "what is this driver still holding?".
                    <Paper
                      key={s.id}
                      variant="outlined"
                      sx={{
                        p: 0,
                        overflow: 'hidden',
                        borderColor: active ? 'primary.main' : 'divider',
                        boxShadow: active ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
                      }}
                    >
                      <ButtonBase
                        onClick={() => {
                          setPackageStatusFilter(active ? 'all' : s.id);
                          setPkgPage(0);
                        }}
                        aria-pressed={active}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          display: 'block',
                          textAlign: 'left',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Chip
                          label={s.label}
                          size="small"
                          sx={{ fontWeight: 600, mb: 1, ...(STATUS_CHIP_STYLES[s.id] || STATUS_CHIP_STYLES.pending) }}
                        />
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          {statusCounts[s.id] || 0}
                        </Typography>
                      </ButtonBase>
                    </Paper>
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Total packages in scope: {analyticsScopedPackages.length}
                {packageStatusFilter !== 'all' && ' — tap a status again to clear the filter'}
              </Typography>
            </Paper>

            {/* The packages themselves, driven by the same scope as the
                breakdown above plus whichever status tile is selected. */}
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mt: { xs: 2, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Assigned Packages {analyticsDriver === 'all' ? '(All Drivers)' : `— ${driverName(analyticsDriver)}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {assignedPackages.length} package{assignedPackages.length === 1 ? '' : 's'}
                    {packageStatusFilter !== 'all'
                      && ` with status "${STATUS_LIST.find(s => s.id === packageStatusFilter)?.label || packageStatusFilter}"`}
                  </Typography>
                </Box>
                {packageStatusFilter !== 'all' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<ClearIcon />}
                    onClick={() => { setPackageStatusFilter('all'); setPkgPage(0); }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Clear status filter
                  </Button>
                )}
              </Stack>

              {assignedPackages.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {analyticsDriver === 'all'
                      ? 'No packages are assigned to any driver in this range.'
                      : `No packages assigned to ${driverName(analyticsDriver)} in this range.`}
                  </Typography>
                </Box>
              ) : (
                <>
                  {pkgError && (
                    <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                      {pkgError}
                    </Typography>
                  )}
                  {/* Rows stay on screen while the next page loads, so the table
                      does not collapse to a spinner and shift the page around. */}
                  <TableContainer
                    aria-busy={pkgLoading}
                    sx={{ opacity: pkgLoading ? 0.6 : 1, transition: 'opacity 160ms ease' }}
                  >
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600, whiteSpace: 'nowrap' } }}>
                          <TableCell>Recipient</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Destination</TableCell>
                          {analyticsDriver === 'all' && <TableCell>Driver</TableCell>}
                          <TableCell align="right">Total</TableCell>
                          <TableCell>Created</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableRows.map(pkg => {
                          const statusKey = (pkg.status || '').toLowerCase();
                          const created = parseAnyDate(pkg.createdAt);
                          return (
                            <TableRow key={pkg.id} hover>
                              <TableCell>
                                {/* `state.from` is the breadcrumb: the package
                                    page sends you back to this exact view. */}
                                <Link
                                  component={RouterLink}
                                  to={`/package/${pkg.id}`}
                                  state={{ from: backToHere, fromLabel: 'Driver Analytics' }}
                                  underline="hover"
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ color: 'text.primary' }}
                                >
                                  {pkg.recipient || 'Unknown recipient'}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={STATUS_LIST.find(s => s.id === statusKey)?.label || pkg.status || 'Unknown'}
                                  size="small"
                                  sx={{ fontWeight: 600, ...(STATUS_CHIP_STYLES[statusKey] || STATUS_CHIP_STYLES.pending) }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{pkg.destination || 'N/A'}</Typography>
                              </TableCell>
                              {analyticsDriver === 'all' && (
                                <TableCell>
                                  <Typography variant="body2">{driverName(pkg.assignedTo)}</Typography>
                                </TableCell>
                              )}
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatCurrency(Number(pkg.total) || Number(pkg.cost) || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {created ? created.toLocaleDateString() : '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    rowsPerPageOptions={PKG_ROWS_PER_PAGE_OPTIONS}
                    count={assignedPackages.length}
                    rowsPerPage={pkgRowsPerPage}
                    page={pkgPage}
                    onPageChange={handlePkgPageChange}
                    onRowsPerPageChange={(e) => {
                      setPkgRowsPerPage(parseInt(e.target.value, 10));
                      setPkgPage(0);
                    }}
                    labelRowsPerPage="Rows per page:"
                    sx={{
                      '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, px: { xs: 1, sm: 2 } },
                    }}
                  />
                </>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DriverDeliveryDashboard;
